import React, { useState, useEffect } from 'react';
import { FolderOpen, Upload, Trash2, FileText, Search, Plus, Folder, MoveRight, RefreshCw } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';

const categories = [
  { value: 'wellness', label: 'General Wellness' },
  { value: 'nutrition', label: 'Nutrition' },
  { value: 'fertility', label: 'Fertility' },
  { value: 'gut_health', label: 'Gut Health' },
  { value: 'vaginal_health', label: 'Vaginal Health' },
  { value: 'inflammation', label: 'Inflammation' },
  { value: 'longevity', label: 'Longevity' },
];

export default function AdminDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFolder, setFilterFolder] = useState('all');
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [movingDoc, setMovingDoc] = useState(null);
  const [uploadData, setUploadData] = useState({
    title: '',
    description: '',
    category: 'wellness',
    folder_id: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes, foldersRes] = await Promise.all([
        adminAPI.getDocuments(),
        adminAPI.getFolders()
      ]);
      setDocuments(docsRes.data.documents || []);
      setFolders(foldersRes.data.folders || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.file || !uploadData.title) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      await adminAPI.uploadDocument(
        uploadData.title, 
        uploadData.category, 
        uploadData.description, 
        uploadData.file,
        uploadData.folder_id || null
      );
      toast.success('Document uploaded successfully');
      setShowUploadDialog(false);
      setUploadData({ title: '', description: '', category: 'wellness', folder_id: '', file: null });
      loadData();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleMoveDocument = async (targetFolderId) => {
    if (!movingDoc) return;
    
    try {
      await adminAPI.moveDocument(movingDoc.id, targetFolderId || null);
      toast.success(targetFolderId ? 'Document moved to folder' : 'Document moved to unorganized');
      setShowMoveDialog(false);
      setMovingDoc(null);
      loadData();
    } catch (error) {
      toast.error('Failed to move document');
    }
  };

  const handleReextract = async (docId, title) => {
    try {
      toast.info(`Re-extracting content from "${title}"...`);
      const response = await adminAPI.reextractDocument(docId);
      toast.success(`Content extracted! ${response.data.content_length} characters`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-extract content');
    }
  };

  const handleDelete = async (docId, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    
    try {
      await adminAPI.deleteDocument(docId);
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getFolderName = (folderId) => {
    if (!folderId) return 'Unorganized';
    const folder = folders.find(f => f.id === folderId);
    return folder?.name || 'Unknown';
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(search.toLowerCase()) ||
      doc.category?.toLowerCase().includes(search.toLowerCase());
    const matchesFolder = filterFolder === 'all' || 
      (filterFolder === 'unorganized' && !doc.folder_id) ||
      doc.folder_id === filterFolder;
    return matchesSearch && matchesFolder;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-documents-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Knowledge Base</h1>
            <p className="text-[#4A2C2A]/60">
              Documents used by the wellness advisor to provide personalized recommendations
            </p>
          </div>
          
          <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
            <DialogTrigger asChild>
              <Button className="bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl" data-testid="upload-document-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Document
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Upload Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={uploadData.title}
                    onChange={(e) => setUploadData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Gut Health Guidelines"
                    required
                    data-testid="doc-title-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={uploadData.category}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Folder (for targeting)</Label>
                    <Select
                      value={uploadData.folder_id || "none"}
                      onValueChange={(value) => setUploadData(prev => ({ ...prev, folder_id: value === "none" ? "" : value }))}
                    >
                      <SelectTrigger data-testid="doc-folder-select">
                        <SelectValue placeholder="Select folder..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Unorganized (General)</SelectItem>
                        {folders.map(folder => (
                          <SelectItem key={folder.id} value={folder.id}>
                            {folder.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={uploadData.description}
                    onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the document content..."
                    rows={3}
                    data-testid="doc-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>File *</Label>
                  <Input
                    type="file"
                    accept=".pdf,.txt,.md,.csv"
                    onChange={(e) => setUploadData(prev => ({ ...prev, file: e.target.files[0] }))}
                    required
                    data-testid="doc-file-input"
                  />
                  <p className="text-xs text-[#4A2C2A]/50">Supported: PDF, TXT, MD, CSV</p>
                </div>
                <Button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120]"
                  data-testid="submit-document-btn"
                >
                  {uploading ? 'Uploading...' : 'Upload Document'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Box */}
        <div className="bg-[#8DA399]/10 rounded-xl p-4 flex items-start gap-3">
          <FolderOpen className="w-5 h-5 text-[#8DA399] mt-0.5" />
          <div className="text-sm text-[#4A2C2A]/70">
            <p className="font-medium text-[#4A2C2A] mb-1">About the Knowledge Base</p>
            <p>
              Documents uploaded here are used by the wellness advisor to provide personalized 
              recommendations. The AI references this content when answering user questions about 
              wellness, nutrition, and health concerns. Documents are kept private and never shared publicly.
            </p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A2C2A]/40" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className="input-warm pl-12"
              data-testid="search-documents-input"
            />
          </div>
          <Select value={filterFolder} onValueChange={setFilterFolder}>
            <SelectTrigger className="w-[200px]" data-testid="filter-folder-select">
              <Folder className="w-4 h-4 mr-2 text-[#4A2C2A]/50" />
              <SelectValue placeholder="Filter by folder" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Documents</SelectItem>
              <SelectItem value="unorganized">Unorganized</SelectItem>
              {folders.map(folder => (
                <SelectItem key={folder.id} value={folder.id}>
                  {folder.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Move Document Dialog */}
        <Dialog open={showMoveDialog} onOpenChange={(open) => { setShowMoveDialog(open); if (!open) setMovingDoc(null); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Move Document to Folder</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-[#4A2C2A]/70">
                Moving: <strong>{movingDoc?.title}</strong>
              </p>
              <p className="text-sm text-[#4A2C2A]/70">
                Select a folder to move this document. Documents in folders are shown to users who match the folder tags.
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => handleMoveDocument(null)}
                >
                  <FolderOpen className="w-4 h-4 mr-2 text-[#4A2C2A]/50" />
                  Unorganized (General)
                </Button>
                {folders.map(folder => (
                  <Button
                    key={folder.id}
                    variant="outline"
                    className={`w-full justify-start ${movingDoc?.folder_id === folder.id ? 'border-[#8DA399] bg-[#8DA399]/10' : ''}`}
                    onClick={() => handleMoveDocument(folder.id)}
                  >
                    <Folder className="w-4 h-4 mr-2 text-[#E8A095]" />
                    {folder.name}
                    {folder.tags?.length > 0 && (
                      <span className="ml-auto text-xs text-[#4A2C2A]/50">
                        {folder.tags.length} tag(s)
                      </span>
                    )}
                  </Button>
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Documents Grid */}
        {filteredDocuments.length === 0 ? (
          <div className="soft-card p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#E8A095]/20 flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-8 h-8 text-[#E8A095]" />
            </div>
            <h2 className="font-heading text-xl text-[#4A2C2A] mb-2">
              {search ? 'No documents found' : 'No Documents Yet'}
            </h2>
            <p className="text-[#4A2C2A]/60 max-w-md mx-auto">
              {search 
                ? 'Try a different search term' 
                : 'Upload wellness documents to build your knowledge base for the wellness advisor.'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="soft-card p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-[#E8A095]" />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setMovingDoc(doc); setShowMoveDialog(true); }}
                      className="text-[#4A2C2A]/50 hover:text-[#4A2C2A]"
                      title="Move to folder"
                      data-testid={`move-doc-${doc.id}`}
                    >
                      <MoveRight className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleReextract(doc.id, doc.title)}
                      className="text-[#8DA399] hover:text-[#8DA399] hover:bg-[#8DA399]/10"
                      title="Re-extract content from file"
                      data-testid={`reextract-doc-${doc.id}`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(doc.id, doc.title)}
                      className="text-[#D68282] hover:text-[#D68282] hover:bg-[#D68282]/10"
                      data-testid={`delete-doc-${doc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                <h3 className="font-medium text-[#4A2C2A] mb-1 line-clamp-1">{doc.title}</h3>
                <p className="text-sm text-[#E8A095] mb-2 capitalize">
                  {doc.category?.replace(/_/g, ' ')}
                </p>
                
                {doc.description && (
                  <p className="text-sm text-[#4A2C2A]/60 mb-3 line-clamp-2">{doc.description}</p>
                )}
                
                {/* Folder info */}
                <div className="flex items-center gap-2 mb-2">
                  <Folder className="w-3.5 h-3.5 text-[#4A2C2A]/40" />
                  <span className={`text-xs ${doc.folder_id ? 'text-[#8DA399]' : 'text-[#4A2C2A]/40'}`}>
                    {getFolderName(doc.folder_id)}
                  </span>
                </div>
                
                <p className="text-xs text-[#4A2C2A]/50">
                  Uploaded {formatDate(doc.created_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
